import React, { Component } from 'react';
import { JsonDocument, Tenant } from 'models/Blip/Index';
import { TenantService } from 'connection/TenantService';
import { MODAL_TYPES, ModalFactory } from 'components/ModalFactory';
import { Permissions } from 'permissions';
import { migrateToCapi } from 'services/CapiService';
import { getOverviewInfo, getTemporaryAccessKey, setConfiguration, setPaymentAccount } from 'services/CeberusApiService';
import { PermissionService } from 'services/PermissionService';
import { OverviewRequest, ConfigurationRequest, CapiRequest } from 'models/request/Index';
import { OverviewResponse } from 'models/response/Index';
import { GridView } from 'models/GridView';
import { Constants } from 'utils/constants';
import { Validations } from 'utils/Validations'
import { Card, List, Button, Tooltip, Icon, Tag, Table } from 'antd';
import { UserDataSingleton } from 'services/UserDataSingleton';
import { AccessKeyRequest } from 'models/request/AccessKeyRequest';
import { PushNotification } from 'utils/PushNotification';

const ListItem = List.Item
const ListItemMeta = List.Item.Meta

const AT = '@';

export class Overview extends Component<OverviewRequest> {

  throughputFormRef: any;
  paymentAccountFormRef: any;
  tenantSelected: Tenant;

  componentDidMount() {
    this.loadPage();
  }

  state = {
    isLoading: true,
    canMigrateToCloudApi: true,
    editingRecord: {},
    currentModal: '',
    isModalLoading: false,
    overviewResponse: new OverviewResponse(),
    gridView: new Array<GridView>(),
    hasEditPermission: PermissionService.hasPermission(Permissions.Application_Overview__Edit)
  }

  loadPage = async () : Promise<void> =>{
    try {
      this.setState({ isLoading: true });
      this.tenantSelected = TenantService.getSelectedTenant();
      const overviewResponse = await getOverviewInfo({ ...this.props, ClusterName: this.tenantSelected.name });
      this.setState({ overviewResponse: overviewResponse });
      const gridView = this.mountingItemList();
      this.setState({ gridView });
    } catch (error) {
      PushNotification.httpError(error);
    }finally{
      this.setState({ isLoading: false });
    }
  }

  addButtonToMigrateForCapi = (gridView: Array<GridView>): Array<GridView> => {
    const organizationIndex = gridView.findIndex(item => item.title.includes('Organization'));

    if(this.state.overviewResponse.CanMigrateToCloudApi){
      gridView.splice(organizationIndex + 1, 0, {
        title: 'WhatsApp:',
        description: [
          `PhoneNumber: ${this.state.overviewResponse.PhoneNumber} `,
          'This number is not in CloudApi:',
          <Button
            key="migrateToCloudApi"
            type="primary"
            shape="round"
            size="small"
            icon="cloud-sync"
            className="ml4"
            disabled={!this.state.overviewResponse.CanMigrateToCloudApi}
            onClick={() => this.handleModalVisibility(MODAL_TYPES.CloudApi, true)}>
            Migrate to CloudApi
          </Button>
        ]
      });
    }
    if(this.state.overviewResponse.PhoneNumber){
      const capiIndex = gridView.findIndex(item => item.title.includes('WhatsApp'));
      const index = capiIndex > 0 ? capiIndex : organizationIndex;
      gridView.splice(index + 1, 0, {
        title: 'Send message to WhatsApp:',
        description: [
          <Button
            key="sendMessageWhatsapp"
            type="primary"
            shape="round"
            size="small"
            icon="robot"
            className="ml4"
            onClick={this.handleSendMessageToWhatsapp}> Send Message </Button>
        ]
      });
    }

    return gridView;
  }

  handleSendMessageToWhatsapp = () : void => {
    window.open(`https://wa.me/${this.state.overviewResponse.PhoneNumber}?text=oi`);
  }

  handleModalVisibility = (type: string, visibility: boolean) : void => {
    switch (type) {
      case MODAL_TYPES.EditPaymentAccount:
      case MODAL_TYPES.EditTenant:
      case MODAL_TYPES.EditConfiguration:
      case MODAL_TYPES.CloudApi:
      case MODAL_TYPES.GetAuthorizationKey:
      case MODAL_TYPES.ShowAuthorizationKey:
        this.setState({ currentModal: visibility ? type : '' });
        break
      default:
        PushNotification.warnig('Unrecognized modal type');
    }

    if (!visibility) {
      this.setState({ editingRecord: {} });
    }
  }

  handleOnEditPaymentAccount = (record: any) : void => {
    if (this.state.hasEditPermission) {
      this.setState({ editingRecord: record });
      this.handleModalVisibility(MODAL_TYPES.EditPaymentAccount, true);
    }
  }

  handleOnEditThroughput = (name: string, value: string) : void => {
    if (this.state.hasEditPermission) {
      this.setState({
        editingRecord: {
          name,
          value,
          owner: this.state.overviewResponse.Identity
        }
      })
      this.handleModalVisibility(MODAL_TYPES.EditConfiguration, true);
    }
  }

  handleMigrateToCloudApi = async () : Promise<void> => {
    this.handleModalVisibility(MODAL_TYPES.CloudApi, false);
    try {
      this.setState({ isLoading: true })

      const request = new CapiRequest();
      request.can_purge =  true;
      request.bot_authorization = this.state.overviewResponse.AuthorizationKey;
      request.bot_organization = this.state.overviewResponse.Organization;

      const response = await migrateToCapi(request);

      PushNotification.success(String(response));
    } catch (error) {
      PushNotification.httpError(error);
    }finally{
      this.setState({ isLoading: false });
    }
  }

  handleSavePaymentAccount = () : void => {
    this.setState({ isModalLoading: true });

    const form = this.paymentAccountFormRef.props.form;

    form.validateFields(async (errors, values) => {
      if (errors) {
        this.setState({ isModalLoading: false });
        throw errors;
      }

      try {
        const { paymentAccount } = values;

        var request = new OverviewRequest();
        request.PaymentAccount = paymentAccount;
        request.BotIdentifier = this.state.overviewResponse.Identity;
        request.ClusterName = this.tenantSelected.name;

        var response = await setPaymentAccount(request);

        form.resetFields();

        this.setState({
          currentModal: '',
          editingRecord: {}
        });

        await this.loadPage();

        this.setState({ isModalLoading: false });

        PushNotification.success(response);
      } catch (error) {
        PushNotification.httpError(error);
      } finally {
        this.setState({ isModalLoading: false });
      }
    })
  }

  handleOnGetAuthorizationKey = () => {
    this.setState({ editingRecord: {
      purpose: '',
      expiration: 900000
    } })
    this.handleModalVisibility(MODAL_TYPES.GetAuthorizationKey, true)
  }

  handleGetAuthorizationKey = async () => {
    this.handleModalVisibility(MODAL_TYPES.GetAuthorizationKey, false)
    this.setState({
      isModalLoading: true
    })
    const { purpose } = this.state.editingRecord as { purpose: string };
    const { expiration } = this.state.editingRecord as { expiration: number };
    const editedPurpose = UserDataSingleton.instance.email + ' - ' + purpose
    const data = await getTemporaryAccessKey(new AccessKeyRequest(this.state.overviewResponse.Identity, editedPurpose, expiration, this.tenantSelected.name))

    this.setState({
      editingRecord: {
        key: data.Key
      }
    })
    this.handleModalVisibility(MODAL_TYPES.ShowAuthorizationKey, true)

    this.setState({
      isModalLoading: false
    })
  }

  renderHeadersDescription = (headers?: { [key: string]: string }) => {
    return headers ? (
      <ul>
        {Object.entries(headers).map(([key, value]) => (
          <li key={key}>
            <strong>{key}:</strong> {value}
          </li>
        ))}
      </ul>
    ) : 'No headers available';
  };

  handleSaveThroughput = async () : Promise<void> => {
    this.setState({ isModalLoading: true });

    const form = this.throughputFormRef.props.form;

    form.validateFields(async (errors, values) => {
      if (errors) {
        this.setState({ isModalLoading: false });
        throw errors;
      }

      try {
        const { owner, name, value } = values;

        const request = new ConfigurationRequest();
        request.BotIdentifier = owner;
        request.Resource = new JsonDocument(name, value);
        request.ClusterName = this.tenantSelected.name;

        const response = await setConfiguration(request);

        form.resetFields();

        this.setState({
          currentModal: '',
          editingRecord: {}
        })

        await this.loadPage();

        this.setState({ isModalLoading: false });
  
        PushNotification.success(response);
      } catch (error) {
        PushNotification.httpError(error);
        throw error;
      } finally {
        this.setState({ isModalLoading: false });
      }
    })
  }

  saveThroughputFormRef = (formRef: any) : void => {
    this.throughputFormRef = formRef
  }

  savePaymentAccountFormRef = (formRef: any) : void => {
    this.paymentAccountFormRef = formRef
  }

  getPaymentAccount = () : string => {
    if(this.state.overviewResponse.PaymentAccount){
      return decodeURIComponent(this.state.overviewResponse.PaymentAccount.split(AT)[0]);
    }

    return '';
  }

  mountingItemList = () : Array<GridView> => {
    if (this.state.overviewResponse) {

      const paymentAccount = this.getPaymentAccount();

      const paymentAccountIsEmail = Constants.EMAIL_REGEX.test(paymentAccount);

      const disablePaymentAccount = this.state.hasEditPermission && paymentAccountIsEmail ? false : true;

      const childrenColumns = [
        {
          title: 'Id',
          dataIndex: 'shortName',
          key: 'shortName',
          render: (text, e) => (
            <p key={e.$id}>
              {text}{' '}
              {e.isDefault === true && (
                <Tooltip title="Main Subbot">
                  {' '}
                  <Icon type="star" theme="twoTone" />
                </Tooltip>
              )}
            </p>
          )
        },
        {
          title: 'Redirect timeout in seconds',
          dataIndex: 'redirectTimeoutInSeconds',
          render: (text, e) =>
            ((e.isPersistent || e.isDefault) && (
              <Tag color="geekblue">Never redirect</Tag>
            )) || <Tag color="green"> {text || '1800'}</Tag>
        }
      ]

      const gridView: Array<GridView> = [
        {
          title: 'Id:',
          description:
          this.state.overviewResponse.Identity != undefined
            ? this.state.overviewResponse.Identity.split(AT)[0]
            : ''
        },
        {
          title: 'Identity:',
          description: this.state.overviewResponse.Identity
        },
        {
          title: 'Name:',
          description: this.state.overviewResponse.Name
        },
        {
          title: 'Cluster:',
          description: this.state.overviewResponse.ClusterName ? this.state.overviewResponse.ClusterName : 'Take'
        },
        {
          title: 'Creation Date:',
          description: this.state.overviewResponse.CreatedAt
        },
        {
          title: 'Authorization Key:',
          description: ['***',
          <Button
            key="getAuthorizationKeyButton"
            type="primary"
            shape="round"
            size="small"
            icon="unlock"
            className="ml4"
            onClick={() => this.handleOnGetAuthorizationKey()}>
            Get Temporary Authorization Key
          </Button>
        ]
        },
        {
          title: 'Organization:',
          description: this.state.overviewResponse.Organization
        },
        {
          title: 'Payment Account:',
          description: [
            paymentAccount,
            <Button
              key="editPlanButton"
              type="primary"
              shape="round"
              size="small"
              icon="edit"
              className="ml4"
              disabled={disablePaymentAccount}
              onClick={() =>
                this.handleOnEditPaymentAccount(
                  this.state.overviewResponse.PaymentAccount
                )}
            >
              Change Payment Account
            </Button>
          ]
        },
        {
          title: 'Account Type:',
          description: this.state.overviewResponse.AccountType
        },
        {
          title: 'BLiP ChatUrl:',
          description: (
            <a href={this.state.overviewResponse.BlipChatUrl} target="_blank">
              Open BLiP Chat
            </a>
          )
        },
        {
          title: 'Status:',
          description: this.state.overviewResponse.Status
        },
        {
          title: 'Connection Type:',
          description: this.state.overviewResponse.ConnectionType
        },
        {
          title: 'URL To Receive Messages:',
          description: this.state.overviewResponse.MessageUrl,
          invisible: Validations.objectIsEmpty(this.state.overviewResponse.MessageUrl)
        },
        {
          title: 'Messages Headers:',
          description: this.renderHeadersDescription(this.state.overviewResponse.MessageHeaders),
          invisible: Validations.objectIsEmpty(this.state.overviewResponse.MessageHeaders)
        },
        {
          title: 'URL To Receive Notifications:',
          description: this.state.overviewResponse.NotificationUrl,
          invisible: Validations.objectIsEmpty(this.state.overviewResponse.NotificationUrl)
        },
        {
          title: 'Notifications Headers:',
          description: this.renderHeadersDescription(this.state.overviewResponse.NotificationHeaders),
          invisible: Validations.objectIsEmpty(this.state.overviewResponse.NotificationHeaders)
        },
        {
          title: 'Router Context:',
          invisible: this.state.overviewResponse.ConnectionType !== Constants.CONNECTION_TYPE_BUILDER,
          description: this.state.overviewResponse.RouterContext
        },
        {
          title: 'Subbots:',
          invisible: this.state.overviewResponse.ConnectionType !== Constants.CONNECTION_TYPE_ROUTER,
          description: (
            <Table
              rowKey="$id"
              dataSource={this.state.overviewResponse.Children}
              columns={childrenColumns}
            />
          )
        },
        {
          title: 'Message Throughput:',
          description: [
            this.state.overviewResponse.MessageThroughput,
            <Button
              key="editMessageThroughputButton"
              type="primary"
              shape="round"
              size="small"
              icon="edit"
              className="ml4"
              disabled={!this.state.hasEditPermission}
              onClick={() =>
                this.handleOnEditThroughput(
                  'MessageThroughput',
                  this.state.overviewResponse.MessageThroughput
                )}
            >
              Change Message Throughput
            </Button>
          ]
        },
        {
          title: 'Notification Throughput:',
          description: [
            this.state.overviewResponse.NotificationThroughput,
            <Button
              key="editNotificationThroughputButton"
              type="primary"
              shape="round"
              size="small"
              icon="edit"
              className="ml4"
              disabled={!this.state.hasEditPermission}
              onClick={() =>
                this.handleOnEditThroughput(
                  'NotificationThroughput',
                  this.state.overviewResponse.NotificationThroughput
                )}
            >
              Change Notification Throughput
            </Button>
          ]
        },
        {
          title: 'Command Throughput:',
          description: [
            this.state.overviewResponse.CommandThroughput,
            <Button
              key="editCommandThroughputButton"
              type="primary"
              shape="round"
              size="small"
              icon="edit"
              className="ml4"
              disabled={!this.state.hasEditPermission}
              onClick={() =>
                this.handleOnEditThroughput(
                  'CommandThroughput',
                  this.state.overviewResponse.CommandThroughput
                )}
            >
              Change Command Throughput
            </Button>
          ]
        }
      ]

      return this.addButtonToMigrateForCapi(gridView);
    }

    return new Array<GridView>();
  }

  render() {
    return (
      <div>
        <ModalFactory
          organization={this.state.overviewResponse.Organization}
          chatbot={this.state.overviewResponse.Identity}
          visible={this.state.currentModal === MODAL_TYPES.CloudApi}
          onCancel={() => this.handleModalVisibility(MODAL_TYPES.CloudApi, false)}
          onOk={this.handleMigrateToCloudApi}
          loading={this.state.isLoading}
          type={this.state.currentModal}
          record={this.state.editingRecord}
          maskClosable={false}
        />

        <Card title="Chatbot Information">
          <List
            loading={this.state.isLoading}
            itemLayout="horizontal"
            dataSource={this.state.gridView}
            renderItem={item =>
              !item.invisible ? (
                <ListItem>
                  <ListItemMeta
                    title={item.title}
                    description={item.description}>
                  </ListItemMeta>
                </ListItem>
              ) : (
                <></>
              )}></List>
        </Card>
        <ModalFactory
          wrappedComponentRef={this.savePaymentAccountFormRef}
          visible={this.state.currentModal === MODAL_TYPES.EditPaymentAccount}
          onCancel={() =>
            this.handleModalVisibility(MODAL_TYPES.EditPaymentAccount, false)}
          onOk={this.handleSavePaymentAccount}
          loading={this.state.isModalLoading}
          type={this.state.currentModal}
          record={this.state.editingRecord}
          maskClosable={false}
        />
        <ModalFactory
          wrappedComponentRef={this.saveThroughputFormRef}
          visible={this.state.currentModal === MODAL_TYPES.EditConfiguration}
          onCancel={() =>
            this.handleModalVisibility(MODAL_TYPES.EditConfiguration, false)}
          onOk={this.handleSaveThroughput}
          loading={this.state.isModalLoading}
          type={this.state.currentModal}
          record={this.state.editingRecord}
          maskClosable={false}
        />
        <ModalFactory
          visible={this.state.currentModal === MODAL_TYPES.GetAuthorizationKey}
          onCancel={() => this.handleModalVisibility(MODAL_TYPES.GetAuthorizationKey, false)}
          loading={this.state.isModalLoading}
          type={this.state.currentModal}
          record={this.state.editingRecord}
          onOk={() => this.handleGetAuthorizationKey()}
          maskClosable={false}
        />
        <ModalFactory
          visible={this.state.currentModal === MODAL_TYPES.ShowAuthorizationKey}
          onCancel={() => this.handleModalVisibility(MODAL_TYPES.ShowAuthorizationKey, false)}
          loading={this.state.isModalLoading}
          type={this.state.currentModal}
          record={this.state.editingRecord}
          onOk={() => this.handleModalVisibility(MODAL_TYPES.ShowAuthorizationKey, false)}
          maskClosable={false}
        />
      </div>
    );
  }

}
